import { useState } from 'react'
import { apiClient } from '@/services/api/client'
import { extractErrorMessage } from '@/utils/errorHandler'

interface ValidityDialogProps {
  isOpen: boolean
  batchId: string
  pendingTeachers: string[]
  assignedTeachers: string[]
  onValiditySet: (validFrom: string | null, validTo: string | null, overlapResults: Record<string, any>) => void
  onSkip: () => void
}

interface OverlapResult {
  trimmed_count: number
  split_count: number
  deleted_count: number
  affected_periods: string[]
}

export function ValidityDialog({ isOpen, batchId, pendingTeachers, assignedTeachers, onValiditySet, onSkip }: ValidityDialogProps) {
  const [validFrom, setValidFrom] = useState<string>('')
  const [validTo, setValidTo] = useState<string>('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [overlapResults, setOverlapResults] = useState<Record<string, OverlapResult> | null>(null)
  const [showPreview, setShowPreview] = useState(false)

  if (!isOpen) return null

  const allTeacherCodes = [...new Set([...pendingTeachers, ...assignedTeachers])]

  const handlePreview = async () => {
    setSubmitting(true)
    setError(null)
    try {
      const res = await apiClient.post(`/api/v1/timetables/v2/batches/${batchId}/set-validity`, {
        valid_from: validFrom || null,
        valid_to: validTo || null,
      })
      setOverlapResults(res.data.overlap_results || {})
      setShowPreview(true)
    } catch (err: unknown) {
      setError(extractErrorMessage(err, { loadError: 'Fehler beim Prüfen der Überschneidungen' }))
    } finally {
      setSubmitting(false)
    }
  }

  const handleConfirm = async () => {
    // Validity was already set by preview, just proceed
    onValiditySet(validFrom || null, validTo || null, overlapResults || {})
  }

  const handleUnlimited = async () => {
    setSubmitting(true)
    setError(null)
    try {
      const res = await apiClient.post(`/api/v1/timetables/v2/batches/${batchId}/set-validity`, {
        valid_from: null,
        valid_to: null,
      })
      onValiditySet(null, null, res.data.overlap_results || {})
    } catch (err: unknown) {
      setError(extractErrorMessage(err, { loadError: 'Fehler beim Setzen der Gültigkeit' }))
      setSubmitting(false)
    }
  }

  const totalAffected = Object.values(overlapResults || {}).reduce(
    (sum, r) => sum + r.trimmed_count + r.split_count + r.deleted_count, 0
  )

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">Gültigkeitsbereich festlegen</h2>

        <p className="text-sm text-gray-600 mb-4">
          Für {allTeacherCodes.length} Lehrer werden Stundenpläne importiert.
          Bitte gib den Gültigkeitsbereich an:
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded text-sm">{error}</div>
        )}

        {!showPreview ? (
          <>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Gültig ab</label>
                <input
                  type="date"
                  value={validFrom}
                  onChange={(e) => setValidFrom(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 border px-3 py-2"
                />
                <p className="text-xs text-gray-400 mt-1">Leer = ab Beginn der Zeit</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Gültig bis</label>
                <input
                  type="date"
                  value={validTo}
                  onChange={(e) => setValidTo(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 border px-3 py-2"
                />
                <p className="text-xs text-gray-400 mt-1">Leer = bis Ende der Zeit</p>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <button
                onClick={onSkip}
                disabled={submitting}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50 disabled:opacity-40"
              >
                Abbrechen
              </button>
              <button
                onClick={handleUnlimited}
                disabled={submitting}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md text-sm hover:bg-gray-300 disabled:opacity-40"
              >
                Unbegrenzt gültig
              </button>
              <button
                onClick={handlePreview}
                disabled={submitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-40"
              >
                {submitting ? 'Prüfen...' : 'Überschneidungen prüfen'}
              </button>
            </div>
          </>
        ) : (
          <>
            {totalAffected > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <div className="text-sm text-yellow-700 font-medium mb-2">
                  ⚠️ {totalAffected} bestehende Einträge werden angepasst:
                </div>
                {Object.entries(overlapResults || {}).map(([code, result]) => (
                  <div key={code} className="text-xs text-yellow-600 mt-1">
                    <strong>{code}:</strong> {result.trimmed_count} beschnitten, {result.split_count} geteilt, {result.deleted_count} entfernt
                    {result.affected_periods.length > 0 && (
                      <ul className="ml-4 mt-1 list-disc">
                        {result.affected_periods.slice(0, 3).map((p, i) => (
                          <li key={i}>{p}</li>
                        ))}
                        {result.affected_periods.length > 3 && (
                          <li>... und {result.affected_periods.length - 3} weitere</li>
                        )}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}

            {totalAffected === 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                <div className="text-sm text-green-700">
                  Keine Überschneidungen mit bestehenden Stundenplänen.
                </div>
              </div>
            )}

            <div className="mb-4 p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
              <div><strong>Gültig ab:</strong> {validFrom || 'Beginn der Zeit'}</div>
              <div><strong>Gültig bis:</strong> {validTo || 'Ende der Zeit'}</div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <button
                onClick={() => setShowPreview(false)}
                disabled={submitting}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50 disabled:opacity-40"
              >
                Zurück
              </button>
              <button
                onClick={handleConfirm}
                disabled={submitting}
                className="px-4 py-2 bg-green-600 text-white rounded-md text-sm hover:bg-green-700 disabled:opacity-40"
              >
                {submitting ? 'Importieren...' : 'Bestätigen und importieren'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
